import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  HTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
  useRef
} from "react";
import cx from "classnames";

import {
  Button,
  type ButtonProps,
  Icon,
  type IconName,
  TooltipWrapper
} from "@theorgchartdev/storybook";

type ActionBase = {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onKeyDown?: ButtonProps["onKeyDown"];
  iconProps?: Partial<ComponentProps<typeof Icon>>;
  disabled?: boolean;
  active?: boolean;
  checkActive?: () => boolean;
  wrapperProps?: ComponentPropsWithoutRef<"span">;
  tooltipProps?: ComponentProps<typeof TooltipWrapper>["tooltipProps"];
  dataTestId?: string;
  buttonProps?: Partial<ButtonProps>;
};

type ActionWithIcon = ActionBase & {
  iconName: IconName;
  content?: never;
};

type ActionWithContent = ActionBase & {
  content: ReactNode;
  iconName?: IconName;
};

export type Action = ActionWithIcon | ActionWithContent;

type Props = HTMLAttributes<HTMLSpanElement> & {
  children?: ReactNode;
  dataTestId?: string;
  actions: Action[];
  customClass?: string;
  spaceBetween?: boolean;
  centerActions?: boolean;
  buttonSize?: ButtonProps["size"];
  visible?: "hover" | "always";
  customStyle?: CSSProperties;
  buttonsInHierarchy?: boolean;
  focusable?: boolean;
};

const EditableItemGroup = ({
  children,
  actions,
  customClass,
  spaceBetween,
  centerActions = true,
  visible = "hover" /* 'hover' | 'always' */,
  customStyle,
  dataTestId,
  buttonSize = "small",
  buttonsInHierarchy = false,
  focusable = true,
  ...otherProps
}: Props) => {
  const baseClass = "ocn-editable-item-group";
  const focusVisibleClass = "ocn-focus-ring";
  const hoverVisibleClass =
    visible === "hover"
      ? buttonsInHierarchy
        ? `${baseClass}--hover-visible-opacity`
        : `${baseClass}--hover-visible-display`
      : null;
  const buttonClass = `${baseClass}--button`;
  const spaceBetweenClass = spaceBetween ? `${baseClass}--space-between` : null;
  const startClass = centerActions === false ? `${baseClass}--start` : null;
  const containerRef = useRef<HTMLSpanElement>(null);

  const isActive = (action: Action) => {
    if (action?.checkActive) {
      let testActive = action.checkActive();
      if (testActive) return testActive;
    }
    return visible === "always" || action.active;
  };

  const hoverButtonClass = visible === "hover" ? `${baseClass}--button-hover-show` : null;

  return (
    <span
      ref={containerRef}
      className={cx(
        baseClass,
        focusVisibleClass,
        spaceBetweenClass,
        startClass,
        customClass,
        hoverVisibleClass
      )}
      style={customStyle}
      data-testid={dataTestId}
      tabIndex={focusable ? 0 : undefined}
      {...otherProps}
    >
      {children}
      {actions && actions.length > 0 ? (
        <div className={`${baseClass}--buttons`}>
          {actions.map((action, idx) => {
            const disabledClass = action.disabled ? `${baseClass}--button--disabled` : null;
            const active = isActive(action);
            return (
              <div key={idx} className={buttonClass} {...action.wrapperProps}>
                {"content" in action ? (
                  <span
                    className={cx(hoverButtonClass, {
                      "ocn-editable-item-group--button-always-visible": active
                    })}
                  >
                    {action.content}
                  </span>
                ) : (
                  <Button
                    className={cx(hoverButtonClass, {
                      "ocn-editable-item-group--button-always-visible": active
                    })}
                    dataTestId={action.dataTestId}
                    onClick={event => {
                      if (!action.disabled) {
                        action.onClick?.(event);
                      }
                      event.stopPropagation();
                    }}
                    onKeyDown={event => {
                      if (!action.disabled) {
                        action.onKeyDown?.(event);
                      }
                      event.stopPropagation();
                    }}
                    tooltipProps={action.tooltipProps}
                    ariaLabel={action.tooltipProps?.message}
                    iconOnly
                    size={buttonSize}
                    modifier="tertiary"
                    disabled={action.disabled}
                    iconProps={{
                      name: action.iconName,
                      ...action.iconProps,
                      className: cx(disabledClass, action.iconProps?.className)
                    }}
                    {...action.buttonProps}
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </span>
  );
};

export default EditableItemGroup;
